import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Check, X, Crown, Zap, Target, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";

const features = [
  { text: "World's 1st AI-Powered Resume Tailoring Technology", highlight: "World's 1st AI-Powered" },
  { text: "Outsmarts the Robots & then IMPRESSES the Humans!", highlight: "Outsmarts the Robots" },
  { text: "Tailor Resume to Specific Job Descriptions in 60 seconds!", highlight: "60 seconds" },
  { text: "Automatically Rewrites Your Entire Resume for YOU!", highlight: "Automatically Rewrites" },
  { text: "Tailor Any Career Doc – Cover Letters, LinkedIn, etc.", highlight: "Any Career Doc" },
  { text: "Guaranteed 90% Resume/Job Match Score", highlight: "Guaranteed 90%" },
  { text: "100% ATS Pass Rate – 80% of Resumes FAIL this Step!", highlight: "100% ATS Pass Rate" },
  { text: "Maintains a Natural & Grammatically Correct Flow", highlight: "Natural & Grammatically Correct" },
  { text: "User-Friendly for a 10yr Old or Non-Tech Savvy Sr. Citizen", highlight: "User-Friendly" },
  { text: "Huge ATS Job Match Score Jumps - BEFORE & AFTER", highlight: "Huge ATS Job Match Score Jumps" },
  { text: "Creates \"Hypnotic-Language-Infused\" Emotional Triggers", highlight: "Hypnotic-Language-Infused" },
  { text: "Avoid \"Keyword Stuffing\" & Generic/Boring Bullets!", highlight: "Avoid Keyword Stuffing" },
  { text: "Comprehensive Job/Industry Keyword Optimization", highlight: "Comprehensive Keyword Optimization" },
  { text: "Eliminates Gaps in Your Skills/Experience – Secret Method!", highlight: "Secret Method" },
  { text: "Personalized Tone & Energy with Natural Speaking Language", highlight: "Personalized Tone & Energy" },
  { text: "PROVEN to 10X Your Odds of Landing Job Interviews!", highlight: "10X Your Odds" },
  { text: "Instantly Mirrors the Scope of Work in Job Description", highlight: "Instantly Mirrors" },
  { text: "Target & Tailor Specific Sections of Your Resume", highlight: "Target & Tailor" },
  { text: "Microsoft Word Style Resume/Document Editor", highlight: "Microsoft Word Style" },
  { text: "Instantly Add Missing Keywords & Experience", highlight: "Instantly Add Missing" },
  { text: "100% \"Done-for-You\" AI Writing Automation", highlight: "Done-for-You" },
  { text: "Job Responsibility & Requirements Breakdown", highlight: "Requirements Breakdown" },
  { text: "Intelligently Removes Irrelevant Experience/Skills!", highlight: "Intelligently Removes" },
  { text: "Expert Guidance & Recommendations", highlight: "Expert Guidance" },
  { text: "100% Honest Qualifications Representation – No Lies!", highlight: "100% Honest" },
  { text: "Create Relevant & Genuine Accomplishment Statements", highlight: "Genuine Accomplishment" },
  { text: "Instantly Download/Save Docs in PDF, MS Word, etc.", highlight: "Instantly Download/Save" },
  { text: "Tailor ENTIRE Resume Packages with Just 1 Click!", highlight: "Just 1 Click" },
  { text: "Built-in Error Handling & Quality Check Features!", highlight: "Quality Check Features" }
];

export default function Features() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center">
              <Crown className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">Why Choose TailorWiz</span>
          </div>
          <div></div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary/10 to-primary-dark/10 py-16 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            COMPETITIVE COMPARISON
          </Badge>
          <h1 className="text-5xl font-bold text-foreground mb-6">
            TailorWiz <span className="text-primary">CRUSHES</span> The Leading Competitors!
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            See why thousands of professionals choose TailorWiz over expensive alternatives. 
            We deliver superior technology at an unbeatable price.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="p-6 text-center">
                <Zap className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg text-green-800 mb-2">Lightning Fast</h3>
                <p className="text-green-700">Tailor your resume in under 60 seconds</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-6 text-center">
                <Target className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg text-blue-800 mb-2">Perfect Match</h3>
                <p className="text-blue-700">Guaranteed 90%+ job match score</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-purple-200 bg-purple-50">
              <CardContent className="p-6 text-center">
                <Shield className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg text-purple-800 mb-2">ATS Guaranteed</h3>
                <p className="text-purple-700">100% ATS pass rate - others fail 80%</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Feature-by-Feature Comparison
            </h2>
            <p className="text-xl text-muted-foreground">
              See why TailorWiz is the clear choice for serious job seekers
            </p>
          </div>

          {/* Comparison Cards for Mobile */}
          <div className="lg:hidden space-y-6">
            <Card className="border-2 border-primary bg-primary/5">
              <CardHeader className="text-center">
                <Crown className="w-8 h-8 text-primary mx-auto mb-2" />
                <CardTitle className="text-2xl text-primary">TailorWiz</CardTitle>
                <div className="text-3xl font-bold text-primary">from $19/month</div>
                <Badge className="bg-green-100 text-green-800 border-green-300">Most Popular</Badge>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm leading-relaxed">
                        <span className="font-semibold text-primary">{feature.highlight}</span>
                        <span className="text-muted-foreground"> - {feature.text.replace(feature.highlight, '').replace(' - ', '').trim()}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <Button className="w-full mt-6 bg-primary hover:bg-primary-dark">
                  Choose TailorWiz
                </Button>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="border border-gray-200">
                <CardHeader className="text-center">
                  <CardTitle className="text-xl text-gray-600">Rezi</CardTitle>
                  <div className="text-2xl font-bold text-gray-600">$29/month</div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {features.slice(0, 10).map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-500 leading-relaxed">{feature.text}</span>
                      </div>
                    ))}
                    <div className="text-sm text-gray-400 italic">...and 19 more missing features</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader className="text-center">
                  <CardTitle className="text-xl text-gray-600">TealHQ</CardTitle>
                  <div className="text-2xl font-bold text-gray-600">$49/month</div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {features.slice(0, 10).map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-500 leading-relaxed">{feature.text}</span>
                      </div>
                    ))}
                    <div className="text-sm text-gray-400 italic">...and 19 more missing features</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block">
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-primary/5 to-primary-dark/5 p-6">
                <div className="grid grid-cols-4 gap-6">
                  <div className="font-semibold text-lg">Features</div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Crown className="w-6 h-6 text-primary" />
                      <span className="text-xl font-bold text-primary">TailorWiz</span>
                    </div>
                    <div className="text-2xl font-bold text-primary">from $19/month</div>
                    <Badge className="bg-green-100 text-green-800 border-green-300 mt-2">Most Popular</Badge>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-600 mb-2">Rezi</div>
                    <div className="text-xl font-bold text-gray-600">$29/month</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-600 mb-2">TealHQ</div>
                    <div className="text-xl font-bold text-gray-600">$49/month</div>
                  </div>
                </div>
              </div>
              
              <CardContent className="p-0">
                {features.map((feature, index) => (
                  <div key={index} className={`grid grid-cols-4 gap-6 p-4 border-b border-border ${index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}`}>
                    <div className="text-sm leading-relaxed">
                      <span className="font-semibold text-foreground">{feature.highlight}</span>
                      <span className="text-muted-foreground"> - {feature.text.replace(feature.highlight, '').replace(' - ', '').trim()}</span>
                    </div>
                    <div className="text-center">
                      <Check className="w-6 h-6 text-green-600 mx-auto" />
                    </div>
                    <div className="text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="bg-gradient-to-r from-green-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-foreground mb-6">
            The Clear Choice for Smart Job Seekers
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">3x</div>
              <div className="text-lg font-semibold mb-2">Cheaper</div>
              <div className="text-muted-foreground">Than expensive competitors</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">29+</div>
              <div className="text-lg font-semibold mb-2">Exclusive Features</div>
              <div className="text-muted-foreground">That others simply don't have</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">60s</div>
              <div className="text-lg font-semibold mb-2">Speed</div>
              <div className="text-muted-foreground">Complete resume tailoring</div>
            </div>
          </div>

          <Card className="bg-white shadow-lg border-2 border-primary/20">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Why Pay More for Less?
              </h3>
              <p className="text-lg text-muted-foreground mb-6">
                While Rezi charges $29/month and TealHQ charges $49/month for basic features, TailorWiz delivers 
                cutting-edge AI technology with 29 exclusive features for just from $19/month.
              </p>
              <Button 
                size="lg" 
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white font-bold px-8 py-4 text-lg"
              >
                Start Your TailorWiz Trial Today
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card/30 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">
            © 2025 TailorWiz. All rights reserved. The smartest choice for resume tailoring.
          </p>
        </div>
      </footer>
    </div>
  );
}